import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString, options = {}) {
  const date = new Date(dateString);
  const defaultOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...options,
  };
  return date.toLocaleDateString('es-VE', defaultOptions);
}

export function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString('es-VE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function formatDateTime(dateString) {
  return `${formatDate(dateString)} a las ${formatTime(dateString)}`;
}

export function getDayName(dateString) {
  const date = new Date(dateString);
  const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return days[date.getDay()];
}

export function generateSessionId() {
  return `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function getStatusBadgeClass(status) {
  const classes = {
    NUEVO: 'badge-nuevo',
    CUALIFICADO: 'badge-cualificado',
    AGENDADO: 'badge-agendado',
    CERRADO: 'badge-cerrado',
    CONFIRMADA: 'bg-green-100 text-green-700',
    CANCELADA: 'bg-red-100 text-red-700',
    REPROGRAMADA: 'bg-yellow-100 text-yellow-700',
    PENDIENTE_SYNC: 'bg-orange-100 text-orange-700',
  };
  return classes[status] || 'bg-slate-100 text-slate-600';
}

export function getPriorityBadgeClass(priority) {
  return priority === 'URGENTE' ? 'badge-urgente' : 'badge-normal';
}

export function getInitials(name) {
  if (!name) return '??';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function truncateText(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function isValidPhone(phone) {
  return /^(\+58|0)?[0-9]{10,11}$/.test(phone.replace(/\s|-/g, ''));
}

/**
 * Catálogo legacy desactivado.
 *
 * Antes aquí vivía un array dental hardcodeado. Lo dejamos vacío para no
 * seguir acoplando el frontend a un catálogo clínico fijo.
 *
 * Regla nueva:
 * - el catálogo real debe venir de backend / config canónica
 * - no de este archivo utilitario
 */
export const SERVICES = [];

/**
 * Helper defensivo por compatibilidad.
 * Recibe cualquier lista y devuelve solo strings válidos, sin vacíos.
 */
export function normalizeCatalogOptions(options = []) {
  if (!Array.isArray(options)) return [];
  return options
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean);
}

export const LEAD_STATUSES = [
  { value: 'NUEVO', label: 'Nuevo' },
  { value: 'CUALIFICADO', label: 'Cualificado' },
  { value: 'AGENDADO', label: 'Agendado' },
  { value: 'CERRADO', label: 'Cerrado' },
];

export const APPOINTMENT_STATUSES = [
  { value: 'CONFIRMADA', label: 'Confirmada' },
  { value: 'CANCELADA', label: 'Cancelada' },
  { value: 'REPROGRAMADA', label: 'Reprogramada' },
  { value: 'PENDIENTE_SYNC', label: 'Pendiente Sync' },
];

export const BRANDING_CACHE_KEY = 'public_site_branding_cache';

export const BRAND_THEME_DEFAULTS = {
  theme_primary: '#C8A95B',
  theme_primary_hover: '#1E293B',
  theme_accent: '#C8A95B',
  theme_soft_bg: '#F8FAFC',
  theme_sidebar_bg: '#020617',
  theme_sidebar_text: '#CBD5E1',
  theme_sidebar_active_bg: '#0F172A',
  theme_sidebar_active_text: '#FFFFFF',
  theme_chat_header_bg: '#0B132B',
  theme_chat_header_text: '#FFFFFF',
  theme_chat_button_bg: '#C8A95B',
  theme_chat_button_text: '#FFFFFF',
  theme_chat_selected_bg: '#F8FAFC',
  theme_chat_selected_text: '#334155',
  theme_chat_selected_border: '#CBD5E1',
  theme_login_button_bg: '#C8A95B',
  theme_login_button_text: '#FFFFFF',
};

export const BRAND_SEMANTIC_DEFAULTS = {
  semantic_danger_bg: '#dc2626',
  semantic_danger_bg_hover: '#b91c1c',
  semantic_danger_text: '#ffffff',
};

export function normalizeHexColor(value, fallback) {
  const raw = String(value || '').trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`;
  }
  return fallback;
}

export function readCachedBranding() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(BRANDING_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function writeCachedBranding(config) {
  if (typeof window === 'undefined' || !config) return;
  try {
    const previous = readCachedBranding() || {};
    window.localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify({ ...previous, ...config }));
  } catch {
    // ignore cache write errors
  }
}

export function buildThemeFromConfig(config = {}) {
  const publicContent =
    config.public_content && typeof config.public_content === 'object'
      ? config.public_content
      : {};

  const source = {
    ...BRAND_THEME_DEFAULTS,
    ...BRAND_SEMANTIC_DEFAULTS,
    ...publicContent,
    ...config,
  };

  const theme = {};

  Object.entries(BRAND_THEME_DEFAULTS).forEach(([key, fallback]) => {
    theme[key] = normalizeHexColor(source[key], fallback);
  });

  Object.entries(BRAND_SEMANTIC_DEFAULTS).forEach(([key, fallback]) => {
    theme[key] = normalizeHexColor(source[key], fallback);
  });

  return theme;
}

export function applyBrandTheme(config = {}) {
  const theme = buildThemeFromConfig(config);

  if (typeof document === 'undefined') return theme;

  const root = document.documentElement;

  root.style.setProperty('--brand-primary', theme.theme_primary);
  root.style.setProperty('--brand-primary-hover', theme.theme_primary_hover);
  root.style.setProperty('--brand-accent', theme.theme_accent);
  root.style.setProperty('--brand-soft-bg', theme.theme_soft_bg);

  root.style.setProperty('--brand-sidebar-bg', theme.theme_sidebar_bg);
  root.style.setProperty('--brand-sidebar-text', theme.theme_sidebar_text);
  root.style.setProperty('--brand-sidebar-active-bg', theme.theme_sidebar_active_bg);
  root.style.setProperty('--brand-sidebar-active-text', theme.theme_sidebar_active_text);

  root.style.setProperty('--brand-chat-header-bg', theme.theme_chat_header_bg);
  root.style.setProperty('--brand-chat-header-text', theme.theme_chat_header_text);
  root.style.setProperty('--brand-chat-button-bg', theme.theme_chat_button_bg);
  root.style.setProperty('--brand-chat-button-text', theme.theme_chat_button_text);
  root.style.setProperty('--brand-chat-selected-bg', theme.theme_chat_selected_bg);
  root.style.setProperty('--brand-chat-selected-text', theme.theme_chat_selected_text);
  root.style.setProperty('--brand-chat-selected-border', theme.theme_chat_selected_border);

  root.style.setProperty('--brand-login-button-bg', theme.theme_login_button_bg);
  root.style.setProperty('--brand-login-button-text', theme.theme_login_button_text);

  root.style.setProperty('--semantic-danger-bg', theme.semantic_danger_bg);
  root.style.setProperty('--semantic-danger-bg-hover', theme.semantic_danger_bg_hover);
  root.style.setProperty('--semantic-danger-text', theme.semantic_danger_text);

  return theme;
}

export function getBrandThemeValue(config = {}, key, fallback = '') {
  const theme = buildThemeFromConfig(config);
  return theme[key] || fallback;
}

const PRELOADED_BRAND_ASSET_URLS = new Set();

export function collectBrandAssetUrls(config = {}) {
  const publicContent = config?.public_content && typeof config.public_content === 'object'
    ? config.public_content
    : {};

  return [
    config?.brand_logo_url,
    config?.landing_hero_image_url,
    config?.landing_secondary_image_url,
    publicContent?.chat_agent_avatar_url,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
}

export function preloadBrandAssetUrls(config = {}) {
  if (typeof window === 'undefined' || typeof Image === 'undefined') return [];

  const urls = collectBrandAssetUrls(config);

  urls.forEach((url) => {
    if (!url || PRELOADED_BRAND_ASSET_URLS.has(url)) return;
    PRELOADED_BRAND_ASSET_URLS.add(url);
    try {
      const img = new Image();
      img.decoding = 'async';
      try {
        img.fetchPriority = 'high';
      } catch {}
      img.src = url;
    } catch {
      PRELOADED_BRAND_ASSET_URLS.delete(url);
    }
  });

  return urls;
}